import { useEffect, useRef, useState } from "react";

/** Zero-padded HH:mm:ss from a total number of seconds. */
export const formatClock = (total) => {
  if (!total || total < 0) total = 0;
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = Math.floor(total % 60);
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(
    s
  ).padStart(2, "0")}`;
};

/** Short, human countdown: "1h 15m", "15m 30s", "45s". */
export const humanize = (total) => {
  if (!total || total < 0) total = 0;
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = Math.floor(total % 60);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
};

/**
 * Locally ticks a countdown down from {@code initialSeconds}, restarting whenever
 * that value changes (e.g. after a status re-fetch). Calls {@code onComplete} once
 * it reaches zero so the caller can refresh the happy-hour state.
 */
export default function useCountdown(initialSeconds, onComplete) {
  const [seconds, setSeconds] = useState(initialSeconds ?? 0);
  const cbRef = useRef(onComplete);
  cbRef.current = onComplete;

  useEffect(() => {
    setSeconds(initialSeconds ?? 0);
    if (!initialSeconds || initialSeconds <= 0) return undefined;

    const id = setInterval(() => {
      setSeconds((prev) => {
        if (prev <= 1) {
          clearInterval(id);
          if (cbRef.current) cbRef.current();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(id);
  }, [initialSeconds]);

  return { seconds, clock: formatClock(seconds), human: humanize(seconds) };
}
